<template>
  <main ref="articleRef" class="ed grow">
    <template v-if="doc">
      <!-- Header and prose share one reading column. Artwork follows the introduction. -->
      <header class="ed-hero">
        <div class="ed-hero__shell">
          <div class="ed-hero__copy">
            <nav class="ed-hero__crumb" aria-label="Breadcrumb">
              <NuxtLink :to="localePath('/blog')">Resources</NuxtLink>
              <span aria-hidden="true">→</span>
              <span>{{ doc.primaryKeyword || 'Guide' }}</span>
            </nav>
            <h1>{{ doc.title }}</h1>
            <div v-if="heroAnswer" class="ed-prose ed-answer" data-answer-first>
              <ContentRenderer :value="heroAnswer" />
            </div>
            <p class="ed-lede">{{ doc.description }}</p>
            <p v-if="guideNotice" class="ed-guide-notice" data-guide-disclosure>{{ guideNotice }}</p>
            <p class="ed-hero__meta">
              <template v-if="showPublished">Published {{ useFormatDate(doc.createdAt) }} · </template>Updated {{ useFormatDate(doc.updatedAt) }} ·
              {{ doc.readingTime?.text || 'Guide' }}
            </p>
            <div class="ed-byline" :class="{ 'ed-byline--photo': isFounder }">
              <img
                v-if="isFounder"
                class="ed-byline__photo"
                src="/images/authors/simon-dziak-160.webp"
                alt="Simon Dziak"
                width="40"
                height="40"
                loading="eager"
                decoding="async"
              />
              <div>
                <p>
                  By <NuxtLink v-if="['Simon Dziak', 'Szymon Dziak'].includes(doc.author?.name)" :to="localePath('/about') + '#founder'">{{ doc.author.name }}</NuxtLink><template v-else>{{ doc.author?.name || 'Workspace369' }}</template><span v-if="doc.author?.role">, {{ doc.author.role }}</span><template v-if="isFounder"> · <a href="https://www.linkedin.com/in/szymondziak/" target="_blank" rel="noopener noreferrer me">LinkedIn</a></template>
                </p>
                <p v-if="doc.reviewer?.name">
                  Reviewed by {{ doc.reviewer.name
                  }}<template v-if="doc.reviewer?.role"> · {{ doc.reviewer.role }}</template>
                </p>
              </div>
            </div>
            <p v-if="headerDisclosure" class="ed-hero__disclosure">{{ headerDisclosure }}</p>
          </div>

        </div>
      </header>

      <div class="ed-col">
        <div class="ed-body">
          <aside class="ed-rail">
            <div class="ed-rail__sticky" :class="`ed-rail--${tocStyle}`">
              <nav v-if="tocLinks.length" class="ed-rail__box" aria-label="What's inside">
                <p>What's inside?</p>
                <div class="ed-rail__list">
                  <a
                    v-for="(link, index) in tocLinks"
                    :key="link.id"
                    :href="`#${link.id}`"
                    class="ed-rail__link"
                    :class="{ 'is-active': activeHeading === link.id }"
                    @click.prevent="scrollToHeading(link.id)"
                  >
                    <span class="ed-rail__num" aria-hidden="true">{{
                      String(index + 1).padStart(2, '0')
                    }}</span>
                    <span class="ed-rail__text">{{ link.text }}</span>
                  </a>
                </div>
              </nav>

              <div class="ed-share">
                <p>Share this article</p>
                <div class="ed-share__row">
                  <button
                    type="button"
                    class="ed-share__btn"
                    :aria-label="copied ? 'Link copied' : 'Copy link'"
                    @click="copyLink"
                  >
                    <svg v-if="!copied" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M10 14a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07l-1.42 1.42" />
                      <path d="M14 10a5 5 0 0 0-7.07 0l-2.83 2.83a5 5 0 0 0 7.07 7.07l1.42-1.42" />
                    </svg>
                    <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M4 12.5l5 5L20 6.5" />
                    </svg>
                  </button>
                  <a
                    :href="linkedinShareUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on LinkedIn"
                    class="ed-share__btn"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8h4v15h-4V8zm7.5 0h3.8v2.05h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V23h-4v-7.9c0-1.88-.04-4.3-2.62-4.3-2.62 0-3.02 2.05-3.02 4.16V23H8V8z" />
                    </svg>
                  </a>
                  <a
                    :href="xShareUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on X"
                    class="ed-share__btn"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M18.9 2H22l-6.77 7.74L23.2 22h-6.24l-4.89-6.4L6.5 22H3.34l7.24-8.28L2.8 2h6.4l4.42 5.84L18.9 2zm-1.1 18.1h1.73L7.08 3.8H5.22l12.58 16.3z" />
                    </svg>
                  </a>
                  <a
                    :href="facebookShareUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on Facebook"
                    class="ed-share__btn"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M13.5 22v-8.2h2.76l.41-3.2H13.5V8.56c0-.93.26-1.56 1.58-1.56h1.7V4.14C16.5 4.1 15.6 4 14.62 4c-2.4 0-4.05 1.47-4.05 4.16v2.44H7.8v3.2h2.77V22h2.93z" />
                    </svg>
                  </a>
                </div>
              </div>

              <!-- Desktop trial box: the rail is the only part of the page
                   that stays on screen while someone reads. -->
              <div class="ed-rail-trial">
                <p>Try Workspace369</p>
                <a
                  :href="addReferralToUrl('https://app.workspace369.com/signup')"
                  class="ed-rail-trial__btn"
                  data-conversion-event="start_free_click"
                  :data-conversion-label="`${conversionLabelPrefix} rail start free`"
                  :data-conversion-location="contentPath"
                >
                  Start a 14-day trial
                </a>
                <TrialDisclosure compact class="ed-rail-trial__note" />
              </div>
            </div>
          </aside>

          <div ref="proseRef" class="ed-content">
            <div v-if="introduction" class="ed-prose ed-intro">
              <ContentRenderer :value="introduction" />
            </div>
            <section v-if="takeaways.length" class="ed-takeaways" aria-labelledby="ed-takeaways-title">
              <h2 id="ed-takeaways-title">Key takeaways</h2>
              <ul>
                <li v-for="item in takeaways" :key="item">{{ item }}</li>
              </ul>
            </section>
            <!-- Quick answers: one short answer per buyer question, each linking the page
                 that answers it in full. Also merged into the post's FAQPage ([slug].vue). -->
            <section v-if="quickAnswers.length" class="ed-quick" aria-labelledby="ed-quick-title">
              <h2 id="ed-quick-title">Quick answers</h2>
              <p v-if="doc.quickAnswersChecked" class="ed-quick__date">Checked {{ useFormatDate(doc.quickAnswersChecked) }}</p>
              <div v-for="item in quickAnswers" :key="item.question" class="ed-quick__item">
                <h3>{{ item.question }}</h3>
                <p>{{ item.answer }}</p>
                <NuxtLink :to="localePath(item.link.path)">{{ item.link.label }} →</NuxtLink>
              </div>
            </section>
            <!-- VIDEO (only where a roundup/comparison video exists for this post: data/comparison-videos.ts) -->
            <ComparisonVideo :path="contentPath" :canonical-url="shareUrl" :page-id="`${shareUrl}#article`" article />
            <details v-if="tocLinks.length" class="ed-inside">
              <summary>What's inside?</summary>
              <a
                v-for="link in tocLinks"
                :key="link.id"
                :href="`#${link.id}`"
                @click.prevent="scrollToHeading(link.id)"
              >
                {{ link.text }}
              </a>
            </details>

          <figure
            v-if="heroVisual"
            class="ed-hero__visual"
            :class="{
              'ed-hero__visual--illustration': heroVisual.kind === 'illustration',
              'ed-hero__visual--generated': heroVisual.kind === 'generated',
            }"
          >
            <img
              :src="heroVisual.src"
              :srcset="heroVisual.srcset || undefined"
              sizes="(min-width: 608px) 560px, calc(100vw - 32px)"
              :alt="heroVisual.alt"
              :width="heroVisual.width || (heroVisual.kind === 'illustration' ? 800 : 1200)"
              :height="heroVisual.height || (heroVisual.kind === 'illustration' ? 600 : 630)"
              loading="lazy"
            />
          </figure>

            <div v-if="bodyDoc" class="ed-prose">
              <template v-if="alsoReadSplit">
                <ContentRenderer :value="alsoReadSplit.before" />
                <BlogAlsoRead :title="alsoReadSplit.link.title" :path="alsoReadSplit.link.path" />
                <PageVideo inline />
                <template v-if="midCtaAfterAlsoRead">
                  <ContentRenderer :value="midCtaAfterAlsoRead.before" />
                  <InlineCta label-prefix="Blog mid-article CTA" />
                  <ContentRenderer :value="midCtaAfterAlsoRead.after" />
                </template>
                <ContentRenderer v-else :value="alsoReadSplit.after" />
              </template>
              <template v-else>
                <template v-if="midCtaSplit">
                  <ContentRenderer :value="midCtaSplit.before" />
                  <InlineCta label-prefix="Blog mid-article CTA" />
                  <ContentRenderer :value="midCtaSplit.after" />
                </template>
                <ContentRenderer v-else :value="bodyDoc" />
                <PageVideo inline />
              </template>
            </div>
          </div>
        </div>

        <nav
          v-if="doc.related?.length || meshLinks.length"
          class="ed-related"
          aria-label="Related guides"
        >
          <template v-if="doc.related?.length">
            <p class="ed-endmatter-label">Related reading</p>
            <ul>
              <li v-for="item in doc.related" :key="item.path">
                <NuxtLink :to="localePath(item.path)" class="ed-related__card">
                  <!-- Decorative: the card's own title names the link. -->
                  <img
                    v-if="ogCoverFor(item.path)"
                    :src="ogCoverFor(item.path)"
                    alt=""
                    width="1200"
                    height="630"
                    loading="lazy"
                  />
                  <span class="ed-related__body">
                    <span class="ed-related__title">{{ item.title }}</span>
                    <span class="ed-related__context">{{ contextFor(item.path) }}</span>
                  </span>
                </NuxtLink>
              </li>
            </ul>
          </template>

          <!-- Mesh fill behind the curated cards — bare links, deduped against
             them. One related section, not two. -->
          <div v-if="meshLinks.length" class="ed-related__more">
            <p class="ed-endmatter-label">More from this topic</p>
            <ul>
              <li v-for="link in meshLinks" :key="link.path">
                <NuxtLink :to="localePath(link.path)">{{ link.title }}</NuxtLink>
              </li>
            </ul>
          </div>
        </nav>

        <section v-if="doc.sources?.length" class="ed-sources" aria-labelledby="ed-sources-title">
          <h2 id="ed-sources-title">Sources reviewed for this guide</h2>
          <ul>
            <li v-for="source in doc.sources" :key="source.url">
              <a :href="source.url" target="_blank" rel="noopener noreferrer">{{ source.title }}</a>
              <span v-if="source.note">{{ source.note }}</span>
            </li>
          </ul>
          <p v-if="doc.disclosure && !headerDisclosure" class="ed-sources__disclosure">{{ doc.disclosure }}</p>
        </section>

        <footer v-if="doc.faq?.length" class="ed-faq">
          <p class="ed-endmatter-label">FAQ</p>
          <div class="ed-faq__list">
            <details v-for="item in doc.faq" :key="item.question" class="ed-faq__item">
              <summary>
                <h3>{{ item.question }}</h3>
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </summary>
              <p>{{ item.answer }}</p>
            </details>
          </div>
        </footer>

        <!-- Product modules: the FeaturesList rail's own links as a quiet
             end-matter band — bare text links, no cards. -->
        <nav class="ed-modules" aria-label="Product modules">
          <p class="ed-endmatter-label">Product modules</p>
          <p class="ed-modules__copy">
            Start with the pieces your team needs today. Add deeper communication, AI, automation,
            accounting, product, inventory, and reporting layers when the operation is ready.
          </p>
          <ul>
            <li v-for="item in featureModules" :key="item.link">
              <NuxtLink :to="localePath(item.link)">{{ item.name }}</NuxtLink>
            </li>
            <li>
              <NuxtLink :to="localePath('/features')" class="ed-modules__all"
                >View all features</NuxtLink
              >
            </li>
          </ul>
        </nav>

        <!-- Next step: an editorial close in the article flow — hairline
             separation, quiet eyebrow, display statement, plain buttons. -->
        <section class="ed-next" aria-label="Start with Workspace369" data-sticky-cta-hide>
          <h2>Run client follow-up in one workspace.</h2>
          <p class="ed-next__copy">
            CRM, inbox, voice, invoicing, payments, projects, files, AI, and workflow automations —
            connected instead of exported.
          </p>
          <div class="ed-next__actions">
            <a
              :href="addReferralToUrl('https://app.workspace369.com/signup')"
              class="ed-next__primary"
              data-conversion-event="start_free_click"
              :data-conversion-label="`${conversionLabelPrefix} start free`"
              :data-conversion-location="contentPath"
            >
              Start free
            </a>
            <!-- Right after the trial button in reading order (B1.1); on wide screens it
                 drops to its own line under both buttons (order + full basis). -->
            <TrialDisclosure class="ed-next__terms" />
            <NuxtLink
              :to="localePath('/pricing')"
              class="ed-next__ghost"
              data-conversion-event="pricing_click"
              :data-conversion-label="`${conversionLabelPrefix} pricing`"
              :data-conversion-location="contentPath"
            >
              See pricing
            </NuxtLink>
          </div>
        </section>
      </div>
    </template>

    <div v-else class="ed-empty">
      <h1>Article preview unavailable</h1>
      <p>The lab post could not be loaded from the content collection.</p>
    </div>
  </main>
</template>

<script setup>
import BlogAlsoRead from '~/components/BlogAlsoRead.vue';
import InlineCta from '~/components/content/InlineCta.vue';
import useFormatDate from '~/composables/useFormatDate';
import { seoRoutes } from '~/data/seo-routes';
import { localizedUrl, stripLocaleFromPath } from '~/data/locales';
import { relatedPagesFor } from '~/data/internal-links';
import { featureListLinks } from '~/components/FeaturesList.vue';
import { normalizeOgRoutePath, ogImagePathForRoute } from '~/utils/og-image';
import { splitIntroduction, splitForAlsoRead, splitForInlineCta, tocLinksFrom } from './blog-design-utils';
import { BLOG_DESIGN_CONTENT_PATH, useBlogDesignDoc } from './useBlogDesignDoc';
import { blogIllustrationForMotif, blogIllustrationMotifForPath } from '~/data/blog-illustrations';
// One generated module per post (modules/page-data.ts), so a post downloads only its own hero image entry.
import blogHeroLoaders from '#build/page-data/blog-hero-loaders.mjs';

/* Shared by the internal lab (/internal/blog-designs/editorial, no props) and
   production (pages/blog/[slug].vue, all props). Lab defaults keep the lab
   page's behavior: own doc fetch, ?toc= query styles, lab conversion labels. */
const props = defineProps({
  post: { type: Object, default: null },
  contentPath: { type: String, default: '' },
  tocStyle: { type: String, default: '' },
  conversionLabelPrefix: { type: String, default: 'Blog design lab editorial' },
  withAlsoRead: { type: Boolean, default: false },
  withMidCta: { type: Boolean, default: false },
});

const { addReferralToUrl } = useReferral();
const { canonicalLocaleCode, localePath } = useWorkspaceLocale();

let lab = null;
if (!props.post) {
  lab = await useBlogDesignDoc();
}
const doc = computed(() => props.post || lab?.doc?.value || null);
const contentPath = computed(() => props.contentPath || lab?.contentPath || BLOG_DESIGN_CONTENT_PATH);
const tocLinks = computed(() => tocLinksFrom(doc.value));

/* Related-list context line, derived from the link target — no invented copy. */
const contextFor = (path) => {
  if (path.startsWith('/compare/')) return 'Head-to-head comparison';
  if (path.startsWith('/solutions/')) return 'Solution overview';
  if (path.startsWith('/features/')) return 'Feature walkthrough';
  return 'Guide';
};

/* Related-card covers: reuse the generated per-route OG images, but only for
   known seo routes — the OG generator covers exactly that set. */
const seoRoutePaths = new Set(seoRoutes.map((route) => normalizeOgRoutePath(route.path)));
const ogCoverFor = (path) => {
  const normalized = normalizeOgRoutePath(path);
  return seoRoutePaths.has(normalized) ? ogImagePathForRoute(normalized) : null;
};

/* Split only the opening text. Never hoist screenshots out of their sections. */
const opening = computed(() => splitIntroduction(doc.value));
// answerFirst: true moves the post's opening paragraph (its pick) under the H1, where AI
// answers quote from; the rest of the introduction stays in the prose column.
const answerSplit = computed(() => {
  const intro = opening.value?.intro;
  const [first, ...others] = intro?.body?.children || [];
  if (!doc.value?.answerFirst || first?.tag !== 'p') return null;
  const slice = (nodes) => ({ ...intro, body: { ...intro.body, children: nodes } });
  return { answer: slice([first]), intro: others.length ? slice(others) : null };
});
const heroAnswer = computed(() => answerSplit.value?.answer || null);
const introduction = computed(() => (answerSplit.value ? answerSplit.value.intro : opening.value?.intro || null));
// Buyer-guide template (rank-ready-buyer-guide): takeaways render once a post has the
// 4-6 the template asks for, so older 3-bullet posts are unchanged until they are rewritten.
const takeaways = computed(() => {
  const list = Array.isArray(doc.value?.takeaways) ? doc.value.takeaways.filter(Boolean) : [];
  return list.length >= 4 ? list : [];
});
const quickAnswers = computed(() => (Array.isArray(doc.value?.quickAnswers)
  ? doc.value.quickAnswers.filter((item) => item?.question && item?.answer && item?.link?.path)
  : []));
// Disclosure belongs next to the byline, unless the body already opens with one.
const headerDisclosure = computed(() => {
  const text = doc.value?.disclosure;
  if (!text) return '';
  return JSON.stringify(doc.value?.body ?? '').includes('Disclosure') ? '' : text;
});
// "Best X" guides rank our own product: say so in the first screen, not only in the byline
// (A35, MKT-6; wording from legal-drafts/in-product-notices.md 11.2).
const guideNotice = computed(() => {
  if (!/(^|\/)best-[^/]+\/?$/.test(String(contentPath.value || ''))) return '';
  const first = Array.isArray(doc.value?.softwareRanking) ? doc.value.softwareRanking[0] : null;
  return first?.name === 'Workspace369'
    ? 'We make Workspace369 and list it first here. Weigh our ranking with that in mind, and if another tool fits you better, use it.'
    : 'We make Workspace369, which is on this list. Weigh our ranking with that in mind.';
});
const isFounder = computed(() => ['Simon Dziak', 'Szymon Dziak'].includes(doc.value?.author?.name));
const showPublished = computed(() => {
  const created = String(doc.value?.createdAt || '').slice(0, 10);
  return Boolean(created && created !== String(doc.value?.updatedAt || '').slice(0, 10));
});
const bodyDoc = computed(() => opening.value?.rest || (opening.value ? null : doc.value));
const leadImage = computed(() => {
  const current = doc.value;
  const src = current?.image || ogCoverFor(contentPath.value);
  // The OG cover repeats the post title shown in the H1, so it is decorative.
  return src ? { src, alt: current?.image ? current.title : '' } : null;
});

const heroSlugFor = (path) =>
  stripLocaleFromPath(String(path || '').split(/[?#]/, 1)[0]).replace(/\/+$/, '').replace(/^\/blog\//, '');
const loadHero = async (path) => (await blogHeroLoaders[heroSlugFor(path)]?.()) || null;
const generatedHero = ref(await loadHero(contentPath.value));
watch(contentPath, async (path) => {
  generatedHero.value = await loadHero(path);
});

/* Retain the legacy motif for internal previews and posts awaiting artwork. */
const illustration = computed(() =>
  doc.value ? blogIllustrationForMotif(blogIllustrationMotifForPath(contentPath.value, doc.value.illustration)) : null,
);

const heroVisual = computed(() => {
  if (generatedHero.value) return { kind: 'generated', ...generatedHero.value };
  if (illustration.value) return { kind: 'illustration', ...illustration.value };
  if (leadImage.value) return { kind: 'screenshot', ...leadImage.value };
  return null;
});

/* Mesh fill under the curated related cards: the same relatedPagesFor() the
   old RelatedPages band used, with the curated paths excluded so nothing
   repeats. relatedPagesFor self-excludes the current path and dedupes by
   title. */
const meshLinks = computed(() => {
  const curated = (Array.isArray(doc.value?.related) ? doc.value.related : [])
    .map((item) => item?.path)
    .filter(Boolean);
  return relatedPagesFor(contentPath.value, 5, curated);
});

/* Product modules band: the top links from the FeaturesList rail's own data
   (its order is the site's priority order), plus the /features hub. */
const featureModules = featureListLinks.slice(0, 6);

/* Preserve the also-read insertion after the second H2 of the article. */
const alsoReadSplit = computed(() =>
  props.withAlsoRead ? splitForAlsoRead(bodyDoc.value) : null,
);

/* Mid-article trial card before the third H2, so phone readers see a trial
   button before the end matter. Posts that already place their own
   `::inline-cta` earlier keep theirs. */
const MID_CTA_BEFORE_H2 = 3;
const midCtaWanted = computed(
  () => props.withMidCta && !!splitForInlineCta(bodyDoc.value, MID_CTA_BEFORE_H2),
);
const midCtaAfterAlsoRead = computed(() => {
  if (!midCtaWanted.value || !alsoReadSplit.value) return null;
  // The also-read split sits right after the second H2's heading, so the
  // third H2 is the first one left in the "after" part.
  return splitForInlineCta(alsoReadSplit.value.after, MID_CTA_BEFORE_H2 - 2);
});
const midCtaSplit = computed(() =>
  midCtaWanted.value && !alsoReadSplit.value
    ? splitForInlineCta(bodyDoc.value, MID_CTA_BEFORE_H2)
    : null,
);

/* Plain thin-bordered figures with honest captions (from alt text).
   Client-side enhancement only — SSR renders the plain img. When the img is
   the paragraph's only content, the figure replaces the paragraph so it
   becomes a direct .ed-prose child and can take the wide grid track. */
const proseRef = ref(null);
const addCaptions = () => {
  proseRef.value?.querySelectorAll('img[alt]').forEach((img) => {
    if (img.closest('figure')) return;
    const paragraph = img.parentNode?.tagName === 'P' ? img.parentNode : null;
    const figure = document.createElement('figure');
    const caption = document.createElement('figcaption');
    caption.textContent = img.getAttribute('alt') || '';
    figure.appendChild(img);
    figure.appendChild(caption);
    if (!paragraph) return;
    /* Hydration leaves comment anchors beside the img — treat whitespace text
       and comment nodes as empty when deciding to hoist the figure. */
    const isIgnorable = (node) =>
      (node.nodeType === 3 && !node.textContent.trim()) || node.nodeType === 8;
    const onlyWhitespaceLeft = [...paragraph.childNodes].every(isIgnorable);
    if (paragraph.childNodes.length === 0 || onlyWhitespaceLeft) {
      paragraph.replaceWith(figure);
    } else {
      paragraph.appendChild(figure);
    }
  });
};

/* Mobile table handling: wrap each table in a plain horizontal-scroll div
   (client-side only — SSR tables already scroll via their own overflow-x).
   The wrap becomes the grid item, so it inherits the wide desktop track. */
const addTableWraps = () => {
  proseRef.value?.querySelectorAll('table').forEach((table) => {
    if (table.parentElement?.classList?.contains('ed-table-wrap')) return;
    const wrap = document.createElement('div');
    wrap.className = 'ed-table-wrap';
    wrap.tabIndex = 0;
    table.parentNode.insertBefore(wrap, table);
    wrap.appendChild(table);
  });
};

/* Lab-only TOC style switch: ?toc=boxed|rule|numbered|filled|rule-boxed on the
   internal route. Read after mount so prerendered SSR always renders the
   default (boxed) and hydration never mismatches; production hardcodes the
   winner via the tocStyle prop, which always wins over the query. */
const route = useRoute();
const TOC_STYLES = ['boxed', 'rule', 'numbered', 'filled', 'rule-boxed'];
const queriedTocStyle = ref('boxed');
const tocStyle = computed(() => props.tocStyle || queriedTocStyle.value);

/* "What's inside?" rail scrollspy: a heading is active while it crosses the
   upper reading band of the viewport. Client-only — SSR renders no active
   state. Same target set (.ed-prose h2[id]) as the production blog's
   scrollspy, via IntersectionObserver instead of a scroll listener. */
const activeHeading = ref('');

/* Share block: the post's canonical URL (same localizedUrl + trailing-slash
   behavior as the page head). Lab pages are internal, so lab shares point at
   the real article. */
const shareUrl = computed(() =>
  localizedUrl('https://workspace369.com', contentPath.value, canonicalLocaleCode.value),
);
const linkedinShareUrl = computed(
  () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl.value)}`,
);
const xShareUrl = computed(
  () =>
    `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl.value)}&text=${encodeURIComponent(doc.value?.title || '')}`,
);
const facebookShareUrl = computed(
  () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl.value)}`,
);

const copied = ref(false);
let copyTimer;

const copyLink = async () => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareUrl.value);
    } else {
      /* non-secure context fallback */
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl.value;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
    }
    copied.value = true;
    clearTimeout(copyTimer);
    copyTimer = setTimeout(() => {
      copied.value = false;
    }, 1500);
  } catch {
    /* clipboard unavailable — noop */
  }
};

let observer = null;
let headerObserver = null;
const articleRef = ref(null);
const headerClearance = () => document.querySelector('.site-header')?.offsetHeight || 117;

const scrollToHeading = (id) => {
  const el = document.getElementById(id);
  if (el) {
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - headerClearance() - 24,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
    history.replaceState(null, '', `#${id}`);
  }
};

onMounted(() => {
  const header = document.querySelector('.site-header');
  if (header) {
    const syncHeaderHeight = () => articleRef.value?.style.setProperty('--ed-header-height', `${headerClearance()}px`);
    syncHeaderHeight();
    headerObserver = new ResizeObserver(syncHeaderHeight);
    headerObserver.observe(header);
  }
  if (!props.tocStyle) {
    const requested = String(route.query.toc || '');
    if (TOC_STYLES.includes(requested)) queriedTocStyle.value = requested;
  }

  addCaptions();
  addTableWraps();

  const headings = document.querySelectorAll('.ed-prose h2[id]');
  if (headings.length) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) activeHeading.value = entry.target.id;
        }
      },
      { rootMargin: '-10% 0px -55% 0px' },
    );
    headings.forEach((heading) => observer.observe(heading));
  }
});

onUnmounted(() => {
  observer?.disconnect();
  headerObserver?.disconnect();
  clearTimeout(copyTimer);
});
</script>

<style scoped>
.ed {
  background: #ffffff;
  color: #0f172a;
  padding-bottom: 0;
}

.ed {
  --ed-gutter: 1rem;
  /* The server-rendered guess at the fixed header (101px phone, 117px desktop at 16px text). In rem because
     the header scales with the text size: in px, 125% phone text made the hero jump 25px when the script
     measured the real header after load. */
  --ed-header-height: 6.3125rem;
}

.ed-col, .ed-hero__shell {
  width: min(100% - var(--ed-gutter) * 2, 44rem);
  margin-inline: auto;
}

.ed-hero {
  padding-top: calc(var(--ed-header-height) + 1.5rem);
}

.ed-hero__copy { min-width: 0; }
.ed-hero__crumb {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  color: #475569;
  font-size: 0.875rem;
  line-height: 1.5;
}
.ed-hero__crumb a { text-decoration: underline; text-underline-offset: 0.2em; }
.ed-hero h1 {
  margin-top: 0.75rem;
  color: #0f172a;
  font-size: 2rem;
  font-weight: 750;
  line-height: 1.15;
  overflow-wrap: break-word;
  hyphens: none;
}
.ed-lede {
  margin-top: 1rem;
  color: #475569;
  font-size: 1.0625rem;
  line-height: 1.6;
}
.ed-answer { margin-top: 1rem; }
.ed-prose.ed-answer :deep(p) { margin: 0; color: #0f172a; font-size: 1.0625rem; line-height: 1.65; }
.dark .ed-prose.ed-answer :deep(p) { color: #f1f5f9; }
.ed-hero__meta, .ed-byline {
  color: #475569;
  font-size: 0.875rem;
  line-height: 1.5;
}
.ed-hero__meta { margin-top: 1rem; }
.ed-byline { margin-top: 0.25rem; }
.ed-byline p { margin: 0; }
.ed-byline--photo { display: flex; align-items: center; gap: 0.625rem; margin-top: 0.5rem; }
.ed-byline__photo { width: 40px; height: 40px; flex: none; border-radius: 9999px; object-fit: cover; }
.ed-byline a { text-decoration: underline; text-underline-offset: 2px; }
.ed-guide-notice {
  margin: 1rem 0 0;
  border-left: 3px solid #0e7490;
  padding: 0.5rem 0.75rem;
  background: #f1f5f9;
  color: #1e293b;
  font-size: 0.9375rem;
  line-height: 1.5;
}
.dark .ed-guide-notice { background: #0f1b2b; color: #e2e8f0; border-left-color: #22d3ee; }
.ed-hero__disclosure {
  margin-top: 0.75rem;
  color: #64748b;
  font-size: 0.8125rem;
  line-height: 1.55;
}
.ed-takeaways {
  margin: 1.5rem 0;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  background: #f8fafc;
}
.ed-takeaways h2 {
  margin: 0 0 0.5rem;
  color: #0f172a;
  font-size: 1rem;
  font-weight: 700;
}
.ed-takeaways ul { margin: 0; padding-left: 1.1rem; list-style: disc; }
.ed-takeaways li { margin: 0.35rem 0; color: #334155; font-size: 0.9375rem; line-height: 1.55; }
.ed-quick {
  margin: 1.5rem 0;
  border-top: 1px solid #e2e8f0;
  padding-top: 1.25rem;
}
.ed-quick h2 {
  margin: 0;
  color: #0f172a;
  font-size: 1.125rem;
  font-weight: 800;
}
.ed-quick__date { margin: 0.25rem 0 0; color: #64748b; font-size: 0.8125rem; }
.ed-quick__item { border-bottom: 1px solid #e2e8f0; padding: 1rem 0; }
.ed-quick__item h3 { margin: 0; color: #0f172a; font-size: 1rem; font-weight: 700; line-height: 1.4; }
.ed-quick__item p { margin: 0.4rem 0; color: #334155; font-size: 0.9375rem; line-height: 1.6; }
.ed-quick__item a { color: #0e7490; font-size: 0.875rem; font-weight: 700; text-decoration: none; overflow-wrap: anywhere; }
.ed-quick__item a:hover { text-decoration: underline; }
.dark .ed-quick, .dark .ed-quick__item { border-color: #334155; }
.dark .ed-quick h2, .dark .ed-quick__item h3 { color: #f8fafc; }
.dark .ed-quick__date { color: #94a3b8; }
.dark .ed-quick__item p { color: #cbd5e1; }
.dark .ed-quick__item a { color: #67e8f9; }
.dark .ed-hero__disclosure { color: #94a3b8; }
.dark .ed-takeaways { border-color: #334155; background: #0f1b2b; }
.dark .ed-takeaways h2 { color: #f8fafc; }
.dark .ed-takeaways li { color: #cbd5e1; }
.ed-hero__visual {
  width: min(100%, 35rem);
  margin: 1.5rem auto;
}
.ed-hero__visual img {
  display: block;
  width: 100%;
  height: auto;
  max-height: 19.6875rem;
  object-fit: contain;
  border-radius: 0.5rem;
}
.dark .ed-hero h1 { color: #f8fafc; }
.dark .ed-lede, .dark .ed-hero__crumb,
.dark .ed-hero__meta, .dark .ed-byline { color: #cbd5e1; }

@media (min-width: 480px) { .ed { --ed-gutter: 1.25rem; } }
@media (min-width: 640px) {
  .ed { --ed-gutter: 1.5rem; }
  .ed-hero h1 { font-size: 2.25rem; }
}
@media (min-width: 768px) { .ed { --ed-header-height: 7.3125rem; } }
@media (min-width: 900px) {
  .ed { --ed-gutter: 2rem; }
  .ed-hero h1 { font-size: 2.5rem; }
  .ed-lede { font-size: 1.125rem; }
}

/* ---------- Body: sticky rail + content column ---------- */
.ed-body {
  padding-top: 1.5rem;
}

.ed-content {
  min-width: 0;
}

/* Mobile/tablet: the rail collapses into a compact details box on top */
.ed-inside {
  margin: 1.25rem 0 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #ffffff;
  padding: 0.85rem 1rem;
}

.ed-inside summary {
  cursor: pointer;
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 850;
}

.ed-inside a {
  display: block;
  margin-top: 0.5rem;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 650;
  line-height: 1.45;
  text-decoration: none;
}

.ed-inside a:hover {
  color: #1ba7ce;
}

/* Desktop rail: release-card idiom box, sticky below the site header
   (measured: lab switcher 51px + sticky header 117px → 8.5rem clears both).
   The sticky wrapper carries the TOC box + share block together; the box caps
   its own height and the link list scrolls internally. */
.ed-rail {
  display: none;
}

.ed-rail__sticky {
  position: sticky;
  top: calc(var(--ed-header-height) + 1.5rem);
  display: grid;
  gap: 0.75rem;
}

.ed-rail__box {
  display: flex;
  flex-direction: column;
  /* Leaves room for the share row and the trial box under it. */
  max-height: calc(100vh - 21rem);
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #ffffff;
  padding: 0.75rem;
}

.ed-rail__box > p {
  flex: none;
}

.ed-rail__list {
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
}

.ed-rail__link {
  display: block;
  border-left: 2px solid transparent;
  margin-top: 0.15rem;
  padding: 0.22rem 0 0.22rem 0.6rem;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1.35;
  text-decoration: none;
  transition: color 120ms ease, border-color 120ms ease;
}

.ed-rail__link:hover {
  color: #1ba7ce;
}

.ed-rail__link.is-active {
  border-left-color: #1ba7ce;
  color: #0f172a;
  font-weight: 700;
}

/* Chapter numbers render in the markup for every style but show only in the
   numbered variant. */
.ed-rail__num {
  display: none;
}

/* ---------- Rail variant: rule (docs-minimal guide line, no box) ---------- */
.ed-rail--rule .ed-rail__box {
  max-height: none;
  border: 0;
  background: transparent;
  padding: 0;
}

.ed-rail--rule .ed-rail__list {
  max-height: calc(100vh - 12rem);
  border-left: 1px solid #e2e8f0;
}

.ed-rail--rule .ed-rail__link {
  /* the 2px active marker overlaps the 1px guide line */
  margin-left: -1px;
  padding-left: calc(0.6rem + 1px);
}

/* ---------- Rail variant: numbered (chapter rows, hairline separated) ------ */
.ed-rail--numbered .ed-rail__box {
  max-height: none;
  border: 0;
  background: transparent;
  padding: 0;
}

.ed-rail--numbered .ed-rail__list {
  max-height: calc(100vh - 12rem);
}

.ed-rail--numbered .ed-rail__link {
  display: flex;
  gap: 0.6rem;
  align-items: baseline;
  margin-top: 0;
  border-top: 1px solid #f1f5f9;
  border-left: 0;
  padding: 0.35rem 0;
}

.ed-rail--numbered .ed-rail__link:first-child {
  border-top: 0;
}

.ed-rail--numbered .ed-rail__num {
  display: block;
  flex: none;
  color: #94a3b8;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
}

.ed-rail--numbered .ed-rail__link.is-active .ed-rail__num {
  color: #1ba7ce;
}

/* ---------- Rail variant: filled (soft slate-50 surface, no border) -------- */
.ed-rail--filled .ed-rail__box {
  border: 0;
  background: #f8fafc;
}

.ed-rail--filled .ed-rail__link {
  border-left: 0;
  border-radius: 0.375rem;
  padding: 0.3rem 0.5rem;
}

.ed-rail--filled .ed-rail__link.is-active {
  background: #ffffff;
  /* the 2px cyan marker — an inset line, not a drop shadow */
  box-shadow: inset 2px 0 0 #1ba7ce;
}

/* ---------- Rail variant: rule-boxed (boxed shell, rule interior, no bold) - */
.ed-rail--rule-boxed .ed-rail__box {
  background: transparent;
}

/* No static guide line: links keep one constant 0.6rem indent in every state
   (no layout shift); the marker exists only on the active row. */
.ed-rail--rule-boxed .ed-rail__link {
  position: relative;
  margin-left: 0;
  border-left: 0;
  padding-left: 0.6rem;
  font-weight: 500;
}

/* Active is signaled only by the cyan marker + cyan text — never bold. The
   marker is a pseudo-element sized to the row's text block (row padding top
   to bottom), not a full-height border. The label is the only strong element
   in the box. */
.ed-rail--rule-boxed .ed-rail__link.is-active {
  color: #1ba7ce;
  font-weight: 500;
}

.ed-rail--rule-boxed .ed-rail__link.is-active::before {
  position: absolute;
  top: 0.22rem;
  bottom: 0.22rem;
  left: 0;
  width: 2px;
  background: #1ba7ce;
  content: "";
}

/* Share: quiet label + 4 hairline icon buttons directly under the rail box */
.ed-share__row {
  display: flex;
  gap: 0.4rem;
  margin-top: 0.6rem;
}

.ed-share__btn {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background: #ffffff;
  color: #475569;
  padding: 0;
  cursor: pointer;
  transition: border-color 150ms ease, color 150ms ease;
}

.ed-share__btn svg {
  width: 1rem;
  height: 1rem;
}

.ed-share__btn:hover {
  border-color: #1ba7ce;
  color: #1ba7ce;
}

/* Trial box under the share row: same hairline card as the TOC box. */
.ed-rail-trial {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #ffffff;
  padding: 0.75rem;
}

.ed-rail-trial > p {
  margin: 0;
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.35;
}

.ed-rail-trial__btn {
  display: flex;
  min-height: 2.5rem;
  align-items: center;
  justify-content: center;
  margin-top: 0.6rem;
  border-radius: 0.375rem;
  background: #0e7490;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 700;
  text-decoration: none;
  transition: background-color 150ms ease;
}

.ed-rail-trial__btn:hover {
  background: #155e75;
  color: #ffffff;
}

.ed-rail-trial__btn:focus-visible {
  outline: 3px solid #269dbb;
  outline-offset: 2px;
}

.ed-rail-trial .ed-rail-trial__note {
  margin: 0.5rem 0 0;
  color: #64748b;
  font-size: 0.75rem;
  line-height: 1.4;
}

.ed-rail__box > p,
.ed-share > p,
.ed-endmatter-label {
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 850;
  letter-spacing: 0;
  text-transform: uppercase;
}

/* ---------- Prose ---------- */
.ed-prose {
  color: #3f4c63;
  font-size: 1.0625rem;
  line-height: 1.7;
}

.ed-prose :deep(h2) {
  margin-top: 4rem;
  margin-bottom: 1.1rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 2.5rem;
  color: #020617;
  font-size: clamp(1.45rem, 2.6vw, 1.8rem);
  font-weight: 880;
  letter-spacing: 0;
  line-height: 1.18;
  scroll-margin-top: calc(var(--ed-header-height) + 1.5rem);
}

.ed-prose :deep(h3) {
  margin-top: 2rem;
  margin-bottom: 0.55rem;
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 850;
  scroll-margin-top: calc(var(--ed-header-height) + 1.5rem);
}

.ed-prose :deep(p) {
  margin: 1.05rem 0;
}

.ed-prose :deep(a) {
  color: #0f172a;
  font-weight: 700;
  text-decoration: underline;
  text-decoration-color: #cbd5e1;
  text-underline-offset: 0.22em;
  transition: color 150ms ease, text-decoration-color 150ms ease;
}

.ed-prose :deep(a:hover) {
  color: #1ba7ce;
  text-decoration-color: #1ba7ce;
}

.ed-prose :deep(strong) {
  color: #0f172a;
  font-weight: 800;
}

.ed-prose :deep(ul),
.ed-prose :deep(ol) {
  display: grid;
  gap: 0.55rem;
  margin: 1.1rem 0;
  padding-left: 1.3rem;
}

.ed-prose :deep(ul li::marker) {
  color: #94a3b8;
}

.ed-prose :deep(ol li::marker) {
  color: #64748b;
  font-weight: 850;
}

.ed-prose :deep(li) {
  color: #3f4c63;
  line-height: 1.7;
}

.ed-prose :deep(table) {
  display: block;
  width: 100%;
  margin: 1.75rem 0;
  overflow-x: auto;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  border-collapse: collapse;
  background: #ffffff;
  font-size: 0.88rem;
  font-variant-numeric: tabular-nums;
}

.ed-prose :deep(thead) {
  background: #f8fafc;
}

.ed-prose :deep(th),
.ed-prose :deep(td) {
  padding: 0.8rem 1rem;
  text-align: left;
  vertical-align: top;
}

.ed-prose :deep(th) {
  border-bottom: 1px solid #e2e8f0;
  color: #334155;
  font-size: 0.7rem;
  font-weight: 850;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.ed-prose :deep(td) {
  border-top: 1px solid #e8eef4;
}

.ed-prose :deep(tbody td:first-child) {
  color: #0f172a;
  font-weight: 700;
}

.ed-prose :deep(tbody tr:first-child td) {
  border-top: 0;
}

/* Mobile scroll wrapper (added client-side by addTableWraps): the wrap becomes
   the grid item and scrolls; the table keeps its own overflow-x as the SSR
   fallback before hydration. */
.ed-prose :deep(.ed-table-wrap) {
  margin: 1.75rem 0;
  overflow-x: auto;
}

.ed-prose :deep(.ed-table-wrap > table) {
  margin: 0;
}

@media (max-width: 640px) {
  .ed-prose :deep(.ed-table-wrap > table) {
    min-width: 36rem;
  }
}

.ed-prose :deep(figure) {
  margin: 2.25rem 0;
}

.ed-prose :deep(img) {
  width: 100%;
  height: auto;
  border: 1px solid #dbe7f0;
  border-radius: 0.5rem;
}

.ed-prose :deep(figcaption) {
  margin-top: 0.65rem;
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.5;
}

.ed-prose :deep(blockquote) {
  margin: 1.75rem 0;
  border-left: 3px solid #1ba7ce;
  padding-left: 1.15rem;
  color: #24344d;
}

.ed-prose :deep(blockquote p) {
  margin: 0;
}

.ed-prose :deep(code) {
  border: 1px solid #e2e8f0;
  border-radius: 0.3rem;
  background: #f8fafc;
  padding: 0.1rem 0.35rem;
  color: #0f172a;
  font-size: 0.88em;
}

/* ---------- End matter ---------- */
.ed-related {
  margin-top: 4.5rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 2.5rem;
}

.ed-related ul {
  display: grid;
  gap: 1rem;
  margin-top: 1.25rem;
}

.ed-related li {
  min-width: 0;
}

/* Related cards: release-card idiom — hairline border, 0.5rem radius, no
   shadow; generated route cover on top, title + one-line context below;
   hover lifts only the border color. */
.ed-related__card {
  display: block;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #ffffff;
  text-decoration: none;
  transition: border-color 150ms ease;
}

.ed-related__card:hover {
  border-color: rgb(27 167 206 / 0.55);
}

.ed-related__card img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-bottom: 1px solid #e2e8f0;
  object-fit: cover;
}

.ed-related__body {
  display: grid;
  gap: 0.2rem;
  padding: 0.9rem 1rem 1.05rem;
}

.ed-related__title {
  color: #0f172a;
  font-size: 0.95rem;
  font-weight: 750;
  line-height: 1.4;
}

.ed-related__context {
  color: #64748b;
  font-size: 0.8rem;
}

@media (min-width: 700px) {
  .ed-related > ul {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Mesh fill under the cards — end-matter list idiom (hairlines, bare links) */
.ed-related__more {
  margin-top: 2.25rem;
}

.ed-related__more ul {
  display: grid;
  margin-top: 1rem;
}

.ed-related__more li {
  border-bottom: 1px solid #f1f5f9;
  padding: 0.6rem 0;
}

.ed-related__more li:first-child {
  padding-top: 0;
}

.ed-related__more li:last-child {
  border-bottom: 0;
}

.ed-related__more a {
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 750;
  text-decoration: none;
}

.ed-related__more a:hover {
  color: #1ba7ce;
}

/* Product modules band — same quiet list idiom */
.ed-modules {
  margin-top: 3rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 2.5rem;
}

.ed-modules__copy {
  max-width: 40rem;
  margin-top: 0.5rem;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.6;
}

.ed-modules ul {
  display: grid;
  margin-top: 1.1rem;
}

.ed-modules li {
  border-bottom: 1px solid #f1f5f9;
  padding: 0.6rem 0;
}

.ed-modules li:first-child {
  padding-top: 0;
}

.ed-modules li:last-child {
  border-bottom: 0;
}

.ed-modules a {
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 750;
  text-decoration: none;
}

.ed-modules a:hover {
  color: #1ba7ce;
}

.ed-modules__all {
  color: #0e7490 !important;
}

.ed-sources {
  margin-top: 3rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 2.5rem;
}

.ed-sources h2 {
  margin-top: 0;
  color: #0f172a;
  font-size: 1.1rem;
  font-weight: 850;
}

.ed-sources ul {
  display: grid;
  margin-top: 1rem;
}

.ed-sources li {
  display: grid;
  gap: 0.15rem;
  border-bottom: 1px solid #f1f5f9;
  padding: 0.75rem 0;
}

.ed-sources li:first-child {
  padding-top: 0;
}

.ed-sources li:last-child {
  border-bottom: 0;
}

.ed-sources a {
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 750;
  text-decoration: none;
}

.ed-sources a:hover {
  color: #1ba7ce;
}

.ed-sources span {
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.5;
}

.ed-sources__disclosure {
  margin-top: 1.25rem;
  border-top: 1px solid #f1f5f9;
  padding-top: 1rem;
  color: #94a3b8;
  font-size: 0.78rem;
  line-height: 1.6;
}

.ed-faq {
  margin-top: 3rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 2.5rem;
  padding-bottom: 1rem;
}

/* FAQ items: the gl-faq treatment from pages/solutions/goals.vue, adapted to
   editorial tokens (slate hairlines, 0.5rem card radius). */
.ed-faq__list {
  display: grid;
  gap: 0.7rem;
  margin-top: 1.25rem;
}

.ed-faq__item {
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #ffffff;
  transition: border-color 150ms ease;
}

.ed-faq__item[open] {
  border-color: rgb(14 116 144 / 0.4);
}

.ed-faq__item summary {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 1.05rem 1.2rem;
  list-style: none;
}

.ed-faq__item summary::-webkit-details-marker {
  display: none;
}

.ed-faq__item summary h3 {
  color: #0f172a;
  font-size: 0.97rem;
  font-weight: 800;
  line-height: 1.35;
}

.ed-faq__item summary svg {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: #94a3b8;
  transition: transform 160ms ease, color 160ms ease;
}

.ed-faq__item[open] summary svg {
  color: #0e7490;
  transform: rotate(180deg);
}

.ed-faq__item > p {
  border-top: 1px solid #f1f5f9;
  padding: 1rem 1.2rem 1.2rem;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.7;
}

/* ---------- Next step (editorial close, in the article flow) ---------- */
.ed-next {
  margin-top: 4rem;
  border-top: 1px solid #e2e8f0;
  padding: 4rem 0 5rem;
}

.ed-next h2 {
  max-width: 50rem;
  margin-top: 0;
  color: #020617;
  font-size: clamp(2.35rem, 5.4vw, 3.85rem);
  font-weight: 880;
  letter-spacing: 0;
  line-height: 1.05;
  text-wrap: balance;
}

.ed-next__copy {
  max-width: 42rem;
  margin-top: 1.25rem;
  color: #475569;
  font-size: 1.05rem;
  line-height: 1.7;
}

.ed-next__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 2.25rem;
}

/* Production button idioms: Cta03/Story solid primary (#1ba7ce → #168fb1),
   FeatureDetailPage's light-surface ghost (1px slate-300, slate-900). */
.ed-next__primary,
.ed-next__ghost {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  /* px: buttons stay 8px at 125% phone text. */
  border-radius: 8px;
  padding: 0.7rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;
}

.ed-next__primary {
  background: #0e7490;
  color: #ffffff;
}

.ed-next__primary:hover {
  background: #155e75;
}

.ed-next__ghost {
  border: 1px solid #cbd5e1;
  color: #0f172a;
}

.ed-next__ghost:hover {
  border-color: #94a3b8;
}

/* The terms sit in the button row right after Start free (reading order). Wide
   screens show them on their own line under both buttons, 9px below as before. */
.ed-next__actions :deep(.ed-next__terms) {
  order: 3;
  flex-basis: 100%;
  /* The full row forces the line break; the padding keeps the 34rem text measure. */
  max-width: none;
  padding-right: max(0px, calc(100% - 34rem));
  margin-top: -0.2rem;
}

/* Phones: the buttons stack, so the row becomes a column in source order:
   Start free, its terms, See pricing (B1.1). */
@media (max-width: 479px) {
  .ed-next__actions {
    flex-direction: column;
    align-items: flex-start;
  }

  .ed-next__actions :deep(.ed-next__terms) {
    order: 0;
    flex-basis: auto;
  }
}

/* Narrow phones at large text: at 2.35rem the final word ("workspace.") is wider
   than the column and its period wrapped onto a line of its own (320px, 125%). */
@media (max-width: 359px) {
  .ed-next h2 {
    font-size: 2rem;
  }
}

.ed-empty {
  width: min(100% - 2rem, 40rem);
  margin: 6rem auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
}

.ed-empty h1 {
  color: #0f172a;
  font-size: 1.6rem;
  font-weight: 850;
}

.ed-empty p {
  margin-top: 0.5rem;
  color: #475569;
}

/* ---------- Dark (release-card dark tokens) ---------- */
.dark .ed {
  background: #020617;
  color: #e2e8f0;
}

.dark .ed-prose :deep(h2),
.dark .ed-prose :deep(h3),
.dark .ed-prose :deep(strong),
.dark .ed-sources h2,
.dark .ed-faq__item summary h3,
.dark .ed-related__title,
.dark .ed-sources a,
.dark .ed-related__more a,
.dark .ed-modules a,
.dark .ed-inside summary,
.dark .ed-rail__link.is-active,
.dark .ed-empty h1 {
  color: #ffffff;
}

.dark .ed-prose,
.dark .ed-prose :deep(p),
.dark .ed-prose :deep(li),
.dark .ed-faq__item > p,
.dark .ed-modules__copy,
.dark .ed-empty p {
  color: #cbd5e1;
}

.dark .ed-endmatter-label,
.dark .ed-rail__box > p,
.dark .ed-share > p,
.dark .ed-related__context,
.dark .ed-sources span,
.dark .ed-lead figcaption,
.dark .ed-prose :deep(figcaption) {
  color: #94a3b8;
}

.dark .ed-sources a:hover,
.dark .ed-related__more a:hover,
.dark .ed-modules a:hover {
  color: #67e8f9;
}

.dark .ed-faq__item {
  border-color: #1e293b;
  background: rgb(15 23 42 / 0.72);
}

.dark .ed-faq__item[open] {
  border-color: rgb(103 232 249 / 0.3);
}

.dark .ed-faq__item[open] summary svg {
  color: #67e8f9;
}

.dark .ed-faq__item > p {
  border-top-color: #1e293b;
}

.dark .ed-related,
.dark .ed-sources,
.dark .ed-faq,
.dark .ed-modules,
.dark .ed-prose :deep(h2) {
  border-top-color: #1e293b;
}

.dark .ed-related__more li,
.dark .ed-modules li {
  border-bottom-color: #16203a;
}

.dark .ed-modules__all {
  color: #67e8f9 !important;
}

.dark .ed-lead img {
  border-color: #1e293b;
}

.dark .ed-rail__box,
.dark .ed-inside {
  border-color: #1e293b;
  background: rgb(15 23 42 / 0.72);
}

.dark .ed-rail--rule .ed-rail__box,
.dark .ed-rail--numbered .ed-rail__box {
  background: transparent;
}

.dark .ed-rail--rule .ed-rail__list {
  border-left-color: #1e293b;
}

.dark .ed-rail--numbered .ed-rail__link {
  border-top-color: #1e293b;
}

.dark .ed-rail--numbered .ed-rail__link.is-active .ed-rail__num {
  color: #67e8f9;
}

.dark .ed-rail--filled .ed-rail__box {
  background: rgb(15 23 42 / 0.72);
}

.dark .ed-rail--filled .ed-rail__link.is-active {
  background: #1e293b;
  box-shadow: inset 2px 0 0 #1ba7ce;
}

.dark .ed-rail--rule-boxed .ed-rail__box {
  background: transparent;
}

.dark .ed-rail--rule-boxed .ed-rail__link.is-active {
  color: #67e8f9;
}

.dark .ed-rail-trial {
  border-color: #1e293b;
  background: rgb(15 23 42 / 0.72);
}

.dark .ed-rail-trial > p {
  color: #edf5fa;
}

.dark .ed-rail-trial .ed-rail-trial__note {
  color: #94a3b8;
}

.dark .ed-share__btn {
  border-color: #1e293b;
  background: rgb(15 23 42 / 0.72);
  color: #cbd5e1;
}

.dark .ed-share__btn:hover {
  border-color: #67e8f9;
  color: #67e8f9;
}

.dark .ed-rail__link,
.dark .ed-inside a {
  color: #94a3b8;
}

.dark .ed-rail__link:hover,
.dark .ed-inside a:hover {
  color: #67e8f9;
}

.dark .ed-rail__link.is-active {
  border-left-color: #1ba7ce;
}

.dark .ed-sources li {
  border-bottom-color: #16203a;
}

.dark .ed-related__card {
  border-color: #1e293b;
  background: rgb(15 23 42 / 0.72);
}

.dark .ed-related__card:hover {
  border-color: rgb(103 232 249 / 0.45);
}

.dark .ed-related__card img {
  border-bottom-color: #1e293b;
}

.dark .ed-prose :deep(a) {
  color: #f8fafc;
  text-decoration-color: #334155;
}

.dark .ed-prose :deep(a:hover) {
  color: #67e8f9;
  text-decoration-color: #67e8f9;
}

.dark .ed-prose :deep(table) {
  border-color: #1e293b;
  background: rgb(15 23 42 / 0.72);
}

.dark .ed-prose :deep(thead) {
  background: #0f172a;
}

.dark .ed-prose :deep(th) {
  border-bottom-color: #1e293b;
  color: #cbd5e1;
}

.dark .ed-prose :deep(td) {
  border-top-color: #1e293b;
}

.dark .ed-prose :deep(tbody td:first-child) {
  color: #f8fafc;
}

.dark .ed-prose :deep(img) {
  border-color: #1e293b;
}

.dark .ed-sources__disclosure {
  border-top-color: #16203a;
  color: #64748b;
}

.dark .ed-prose :deep(blockquote) {
  color: #dbe7f3;
}

.dark .ed-prose :deep(code) {
  border-color: #1e293b;
  background: #0f172a;
  color: #f8fafc;
}

.dark .ed-next {
  border-top-color: #1e293b;
}

.dark .ed-next h2 {
  color: #ffffff;
}

.dark .ed-next__copy {
  color: #cbd5e1;
}

.dark .ed-next__ghost {
  border-color: #334155;
  color: #e2e8f0;
}

.dark .ed-next__ghost:hover {
  border-color: #64748b;
}

.dark .ed-empty {
  border-color: #1e293b;
}

/* Same reading track for title, description and body, including without a TOC. */
.ed-intro :deep(> div > :first-child) { margin-top: 0; }
.ed-intro :deep(> div > :last-child) { margin-bottom: 0; }
.ed :deep(:focus-visible) { outline: 2px solid #0891b2; outline-offset: 4px; }
@media (min-width: 640px) { .ed-prose { font-size: 1.125rem; } }
@media (min-width: 1280px) {
  .ed-col, .ed-hero__shell { width: min(100% - 4rem, 72rem); }
  .ed-hero__shell, .ed-body {
    display: grid;
    grid-template-columns: 12rem minmax(0, 44rem) minmax(0, 1fr);
    column-gap: 2rem;
    align-items: start;
  }
  .ed-hero__copy, .ed-content { grid-column: 2; }
  .ed-hero h1 { font-size: 2.625rem; }
  .ed-rail { display: block; align-self: stretch; }
  .ed-inside { display: none; }
  .ed-related ul { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1.25rem; }
  .ed-faq { max-width: 56rem; margin-inline: auto; }
}

.ed-prose :deep(.ed-table-wrap:focus-visible) { outline: 2px solid #0e7490; outline-offset: 3px; }
.dark .ed-prose :deep(.ed-table-wrap:focus-visible) { outline-color: #67e8f9; }
</style>
